import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Send, Loader2, ChevronDown, FileText, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WhatsApp compose modal — uses pre-approved templates from Meta.
 *
 * Free-form text is only allowed inside the 24-hour customer-service window
 * (i.e. customer messaged us first within the last 24h). Outside that window
 * Meta requires a registered template — same as how the invoice flow works.
 *
 * Templates hardcoded match what the user submitted in Meta Business Manager:
 *   - invoice_ready          (3 body params + Pay Now URL button)
 *   - appointment_reminder   (3 body params, no buttons)
 *   - payment_reminder       (2 body params + Pay Now URL button)
 *   - general_message        (2 body params, no buttons)
 *
 * Each template's parameter labels are derived from the template body so the
 * user knows what each {{1}} {{2}} {{3}} represents without having to look it up.
 */
const TEMPLATES = [
  {
    id: 'invoice_ready',
    name: 'Invoice Ready',
    description: 'Send an invoice with a Pay Now button',
    body_preview: 'Hi {{name}}, your invoice for £{{amount}} is ready. View invoice: {{link}} Thanks - Perfect Panes',
    body_param_labels: ['Customer first name', 'Amount (£)', 'Invoice link'],
    button_param_label: 'Invoice ID (for Pay Now button)',
    has_button: true,
  },
  {
    id: 'appointment_reminder',
    name: 'Appointment Reminder',
    description: 'Remind customer of an upcoming visit',
    body_preview: "Hi {{name}}, just a reminder we'll be over for your {{service}} on {{date}}. Thanks - Perfect Panes",
    body_param_labels: ['Customer first name', 'Service (e.g. Window Clean)', 'Date (e.g. Tuesday 30 April)'],
    has_button: false,
  },
  {
    id: 'payment_reminder',
    name: 'Payment Reminder (Pay Now)',
    description: 'Chase an overdue customer with a Pay Now button',
    body_preview: 'Hi {{name}}, you have an outstanding balance of £{{amount}}. Tap below to pay now.',
    body_param_labels: ['Customer first name', 'Outstanding amount (£)'],
    button_param_label: 'Invoice ID (for Pay Now button)',
    has_button: true,
  },
  {
    id: 'general_message',
    name: 'General Message',
    description: 'A short personalised message',
    body_preview: 'Hi {{name}}, {{body}}',
    body_param_labels: ['Customer first name', 'Message body'],
    has_button: false,
  },
];

const WhatsAppComposeModal = ({
  isOpen,
  onClose,
  recipient = null, // { id, name, phone, customer_id, last_invoice_id?, last_invoice_amount?, balance? }
  defaultTemplate = 'general_message',
  prefill = {}, // { body_params: [...], button_url_param: '...' }
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(defaultTemplate);
  const [bodyParams, setBodyParams] = useState([]);
  const [buttonUrlParam, setButtonUrlParam] = useState('');
  const [sending, setSending] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [waConfigured, setWaConfigured] = useState(null); // null=loading, true/false

  const selectedTemplate = TEMPLATES.find(t => t.id === selectedTemplateId);

  // Check WhatsApp config on open
  useEffect(() => {
    if (!isOpen) return;
    const checkConfig = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API}/api/whatsapp/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setWaConfigured(data.configured === true);
        } else {
          setWaConfigured(false);
        }
      } catch {
        setWaConfigured(false);
      }
    };
    checkConfig();
  }, [isOpen]);

  // Set sensible default body params based on the recipient + selected template
  const seedDefaultParams = useCallback(() => {
    const firstName = recipient?.name?.split(' ')[0] || '';
    const amountStr = (recipient?.last_invoice_amount ?? recipient?.balance ?? 0).toFixed(2);
    const tpl = TEMPLATES.find(t => t.id === selectedTemplateId);
    if (!tpl) return;

    let defaultBody = [];
    let defaultButton = '';
    switch (selectedTemplateId) {
      case 'invoice_ready': {
        const link = recipient?.last_invoice_id
          ? `https://app.streakfree.app/invoice/${recipient.last_invoice_id}`
          : '';
        defaultBody = [firstName, amountStr, link];
        defaultButton = recipient?.last_invoice_id || '';
        break;
      }
      case 'appointment_reminder':
        defaultBody = [firstName, 'Window Clean', ''];
        break;
      case 'payment_reminder':
        defaultBody = [firstName, amountStr];
        defaultButton = recipient?.last_invoice_id || '';
        break;
      case 'general_message':
        defaultBody = [firstName, ''];
        break;
      default:
        defaultBody = tpl.body_param_labels.map(() => '');
    }
    // Apply prefill on top (so caller can override defaults)
    if (prefill.body_params && Array.isArray(prefill.body_params)) {
      defaultBody = prefill.body_params;
    }
    if (prefill.button_url_param) {
      defaultButton = prefill.button_url_param;
    }
    setBodyParams(defaultBody);
    setButtonUrlParam(defaultButton);
  }, [recipient, selectedTemplateId, prefill]);

  useEffect(() => {
    if (isOpen) {
      seedDefaultParams();
    }
  }, [isOpen, selectedTemplateId, seedDefaultParams]);

  const handleSend = async () => {
    if (!recipient?.phone) {
      toast.error('Recipient has no phone number');
      return;
    }
    if (selectedTemplate.body_param_labels.some((_, i) => !bodyParams[i]?.trim())) {
      toast.error('Please fill all template fields');
      return;
    }
    if (selectedTemplate.has_button && !buttonUrlParam.trim()) {
      toast.error('Please provide the button URL value (usually the invoice ID)');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API}/api/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_phone: recipient.phone,
          template_name: selectedTemplate.id,
          body_params: bodyParams,
          button_url_param: selectedTemplate.has_button ? buttonUrlParam : null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'sent') {
        toast.success(`WhatsApp ${selectedTemplate.name.toLowerCase()} sent to ${recipient.name}`);
        onClose(true);
      } else {
        const detail = data.detail
          ? (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail))
          : 'Send failed';
        toast.error(`WhatsApp send failed: ${detail.substring(0, 200)}`);
      }
    } catch (err) {
      console.error('WhatsApp send error', err);
      toast.error('Failed to send WhatsApp message');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[130] flex items-end sm:items-center justify-center"
      onClick={() => onClose(false)}
      data-testid="wa-compose-overlay"
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md modal-shell-h overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-5 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#25d366]/15 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#25d366]" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-zinc-900">Send WhatsApp</h3>
              <p className="text-xs text-zinc-500">to {recipient?.name}</p>
            </div>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-2 hover:bg-zinc-100 rounded-xl transition-colors"
            data-testid="wa-compose-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Config warning */}
        {waConfigured === false && (
          <div className="mx-5 mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 items-start">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              WhatsApp is not configured on the backend. Add <code className="text-xs bg-amber-100 px-1 rounded">WHATSAPP_ACCESS_TOKEN</code> and <code className="text-xs bg-amber-100 px-1 rounded">WHATSAPP_PHONE_NUMBER_ID</code> to env.
            </div>
          </div>
        )}

        {/* Phone */}
        {!recipient?.phone && (
          <div className="mx-5 mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
            This customer has no phone number. Add one to send WhatsApp messages.
          </div>
        )}

        {/* Template selector */}
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">Template</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-xl transition-colors text-left"
                data-testid="wa-template-selector"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-zinc-500 flex-shrink-0" />
                  <span className="font-medium text-zinc-900 truncate">{selectedTemplate?.name}</span>
                </div>
                <ChevronDown size={16} className={`text-zinc-500 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showTemplateDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-200 rounded-xl shadow-lg z-20 overflow-hidden">
                  {TEMPLATES.map(tpl => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => {
                        setSelectedTemplateId(tpl.id);
                        setShowTemplateDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-b-0 ${selectedTemplateId === tpl.id ? 'bg-emerald-50' : ''}`}
                      data-testid={`wa-template-${tpl.id}`}
                    >
                      <div className="font-medium text-zinc-900">{tpl.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{tpl.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Body param inputs */}
          {selectedTemplate.body_param_labels.map((label, idx) => (
            <div key={idx}>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                {label}
              </label>
              {label.toLowerCase().includes('body') || label.toLowerCase().includes('message') ? (
                <textarea
                  value={bodyParams[idx] || ''}
                  onChange={(e) => {
                    const next = [...bodyParams];
                    next[idx] = e.target.value;
                    setBodyParams(next);
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#25d366]/40"
                  data-testid={`wa-body-param-${idx}`}
                />
              ) : (
                <input
                  type="text"
                  value={bodyParams[idx] || ''}
                  onChange={(e) => {
                    const next = [...bodyParams];
                    next[idx] = e.target.value;
                    setBodyParams(next);
                  }}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#25d366]/40"
                  data-testid={`wa-body-param-${idx}`}
                />
              )}
            </div>
          ))}

          {/* Button URL param */}
          {selectedTemplate.has_button && (
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                {selectedTemplate.button_param_label}
              </label>
              <input
                type="text"
                value={buttonUrlParam}
                onChange={(e) => setButtonUrlParam(e.target.value)}
                placeholder="e.g. invoice ID UUID"
                className="w-full px-3 py-2 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#25d366]/40 font-mono"
                data-testid="wa-button-url-param"
              />
              <p className="text-xs text-zinc-500 mt-1">
                The Pay Now button will redirect to your Stripe checkout for this invoice.
              </p>
            </div>
          )}

          {/* Preview */}
          <div className="bg-[#dcf8c6] rounded-xl p-3 mt-2">
            <div className="text-xs text-zinc-600 mb-1 font-medium">Preview</div>
            <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">
              {(() => {
                let text = selectedTemplate.body_preview;
                const placeholders = selectedTemplate.body_preview.match(/\{\{[a-z]+\}\}/g) || [];
                placeholders.forEach((ph, i) => {
                  text = text.replace(ph, bodyParams[i] || ph);
                });
                return text;
              })()}
            </p>
            {selectedTemplate.has_button && (
              <div className="mt-2 pt-2 border-t border-emerald-200/40 text-center">
                <span className="text-[#25d366] text-sm font-medium">↗ Pay Now</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer / Send button */}
        <div className="sticky bottom-0 bg-white border-t border-zinc-100 px-5 py-4">
          <button
            onClick={handleSend}
            disabled={sending || !recipient?.phone || waConfigured === false}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#25d366] hover:bg-[#1ebe5b] disabled:bg-zinc-300 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors"
            data-testid="wa-send-btn"
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send size={18} />
                Send WhatsApp
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppComposeModal;
